/**
 * Hard-delete confirmation for a campaign report page (type the slug). Moved
 * as-is from the original tab.
 */

import React, { useState } from 'react';
import { Button, Modal } from '../../ui';
import { CampaignReportPage, deleteCampaignReportPage } from '../../../services/campaignReportsApi';
import { Notify, fieldStyle, labelStyle } from './shared';

interface CampaignDeleteModalProps {
  page: CampaignReportPage;
  onClose: () => void;
  onDeleted: () => void;
  notify: Notify;
}

const CampaignDeleteModal: React.FC<CampaignDeleteModalProps> = ({ page, onClose, onDeleted, notify }) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const run = async () => {
    if (confirmText !== page.slug) return;
    setDeleting(true);
    try {
      await deleteCampaignReportPage(page.slug);
      notify('success', 'Page deleted', `${page.clientLabel} is gone — the URL now shows “not found.”`);
      onDeleted();
      onClose();
    } catch (err: any) {
      notify('error', 'Delete failed', err?.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Campaign reports"
      title={`Delete — ${page.clientLabel}`}
      width={500}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={run} disabled={deleting || confirmText !== page.slug}>
            {deleting ? 'Deleting…' : 'Delete page forever'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
        <div style={{ color: 'var(--danger)', fontWeight: 600 }}>
          This permanently deletes the page, its URL and the stored report data — clients will see “not found”
          immediately. It cannot be undone.
        </div>
        <div style={{ color: 'var(--fg-3)' }}>
          If you might need it again, use <strong>Revoke</strong> instead — that keeps the page and lets you restore it later.
        </div>
        {page.advertiserPageSlug && (
          <div style={{ color: 'var(--fg-3)' }}>
            This page is listed on the <strong>/advertisers/{page.advertiserPageSlug}/reports</strong> hub; it disappears from
            there too.
          </div>
        )}
        <div>
          <label style={labelStyle}>
            Type the slug to confirm: <code>{page.slug}</code>
          </label>
          <input style={fieldStyle} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={page.slug} />
        </div>
      </div>
    </Modal>
  );
};

export default CampaignDeleteModal;
